import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { useDispatch } from 'react-redux'
import { Modal } from '@mui/material'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import { getPortalPaymentMethods, addPortalBankAccount, addPortalCreditCard } from '../../features/portal/portal'
import { LuX } from 'react-icons/lu'
import { ACH, acceptsAch, acceptsCard, defaultMethodOf } from '../../constants/paymentMethods'

const INITIAL_BANK_FORM = {
  bankName: '', nameOnAccount: '', routingNumber: '', accountNumber: '',
  address: '', address2: '', city: '', state: '', zipcode: '',
}

const INITIAL_CARD_FORM = {
  cardNumber: '', nameOnCard: '', cvv: '', expiryDate: '', provider: 'Visa',
  address: '', address2: '', city: '', state: '', zipcode: '',
}

const PortalPaymentMethods = forwardRef(({ hideHeader = false }, ref) => {
  const dispatch = useDispatch()
  const [methods, setMethods] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('bank')
  const [submitting, setSubmitting] = useState(false)
  const [bankForm, setBankForm] = useState(INITIAL_BANK_FORM)
  const [cardForm, setCardForm] = useState(INITIAL_CARD_FORM)
  const [errors, setErrors] = useState({})

  const showBank = acceptsAch(methods?.acceptedPaymentMethods)
  const showCard = acceptsCard(methods?.acceptedPaymentMethods)
  const defaultTab = defaultMethodOf(methods?.acceptedPaymentMethods) === ACH ? 'bank' : 'card'
  const activeTabAccepted = (activeTab === 'bank' && showBank) || (activeTab === 'card' && showCard)
  const currentTab = activeTabAccepted ? activeTab : defaultTab

  useImperativeHandle(ref, () => ({
    openModal: () => setModalOpen(true),
  }))

  useEffect(() => {
    fetchMethods()
  }, [])

  const fetchMethods = async () => {
    const result = await dispatch(getPortalPaymentMethods())
    if (result?.error) {
      showSnackBar('error', result?.payload || 'Failed to load payment methods')
    } else if (result?.payload?.data) {
      setMethods(result.payload.data)
    }
  }

  const updateBankField = (field, value) => {
    setBankForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const updateCardField = (field, value) => {
    setCardForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const validateBank = () => {
    const errs = {}
    if (!bankForm.bankName.trim()) errs.bankName = 'Bank name is required'
    if (!bankForm.nameOnAccount.trim()) errs.nameOnAccount = 'Name on account is required'
    if (!bankForm.routingNumber.trim()) errs.routingNumber = 'Routing number is required'
    else if (bankForm.routingNumber.length !== 9) errs.routingNumber = 'Routing number must be 9 digits'
    if (!bankForm.accountNumber.trim()) errs.accountNumber = 'Account number is required'
    else if (bankForm.accountNumber.length < 3) errs.accountNumber = 'Account number must be at least 3 characters'
    if (!bankForm.address.trim()) errs.address = 'Address is required'
    if (!bankForm.city.trim()) errs.city = 'City is required'
    if (!bankForm.state.trim()) errs.state = 'State is required'
    if (!bankForm.zipcode.trim()) errs.zipcode = 'Zip code is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const validateCard = () => {
    const errs = {}
    if (!cardForm.cardNumber.trim()) errs.cardNumber = 'Card number is required'
    else if (cardForm.cardNumber.replace(/\s/g, '').length < 13) errs.cardNumber = 'Invalid card number'
    if (!cardForm.nameOnCard.trim()) errs.nameOnCard = 'Name on card is required'
    if (!cardForm.cvv.trim()) errs.cvv = 'CVV is required'
    else if (cardForm.cvv.length < 3 || cardForm.cvv.length > 4) errs.cvv = 'CVV must be 3 or 4 digits'
    if (!cardForm.expiryDate.trim()) errs.expiryDate = 'Expiry date is required'
    if (!cardForm.address.trim()) errs.address = 'Address is required'
    if (!cardForm.city.trim()) errs.city = 'City is required'
    if (!cardForm.state.trim()) errs.state = 'State is required'
    if (!cardForm.zipcode.trim()) errs.zipcode = 'Zip code is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmitBank = async (e) => {
    e.preventDefault()
    if (!validateBank()) return
    setSubmitting(true)
    const result = await dispatch(addPortalBankAccount(bankForm))
    if (result?.error) {
      showSnackBar('error', result?.payload || 'Failed to add bank account')
    } else {
      showSnackBar('success', 'Bank account added successfully')
      setBankForm(INITIAL_BANK_FORM)
      setModalOpen(false)
      fetchMethods()
    }
    setSubmitting(false)
  }

  const handleSubmitCard = async (e) => {
    e.preventDefault()
    if (!validateCard()) return
    setSubmitting(true)
    const result = await dispatch(addPortalCreditCard(cardForm))
    if (result?.error) {
      showSnackBar('error', result?.payload || 'Failed to add credit card')
    } else {
      showSnackBar('success', 'Credit card added successfully')
      setCardForm(INITIAL_CARD_FORM)
      setModalOpen(false)
      fetchMethods()
    }
    setSubmitting(false)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setErrors({})
  }

  const renderInput = (label, field, value, onChange, type = 'text', placeholder = '') => (
    <div>
      <label className="text-[13px] text-text-secondary block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
        className={`w-full border rounded-xl px-3 py-2 text-sm text-text-primary bg-paper focus:outline-none focus:border-primary-main focus:ring-1 focus:ring-primary-main ${errors[field] ? 'border-error-main' : 'border-divider'}`}
      />
      {errors[field] && <p className="text-xs text-error-main mt-1">{errors[field]}</p>}
    </div>
  )

  return (
    <>
      {!hideHeader && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-md text-text-primary">Payment Methods</h3>
          <button
            onClick={() => setModalOpen(true)}
            className="px-4 py-2 bg-primary-main text-primary-contrast text-sm font-medium rounded-xl hover:opacity-90"
          >
            Add Payment Method
          </button>
        </div>
      )}

      {/* Bank Accounts */}
      {methods?.bankAccounts?.map(ba => (
        <div key={ba._id} className="border border-divider rounded-xl p-4 bg-surface mb-2 flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-text-primary">{ba.bankName}</p>
            <p className="text-xs text-text-secondary">ACH ****{ba.accountNumberLast4}</p>
          </div>
          {ba.isPrimary && (
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-success-light text-success-main">Primary</span>
          )}
        </div>
      ))}

      {/* Credit Cards */}
      {methods?.creditCards?.map(cc => (
        <div key={cc._id} className="border border-divider rounded-xl p-4 bg-surface mb-2 flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-text-primary capitalize">{cc.provider || 'Card'}</p>
            <p className="text-xs text-text-secondary">****{cc.cardNumberLast4}</p>
          </div>
          {cc.isPrimary && (
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-success-light text-success-main">Primary</span>
          )}
        </div>
      ))}

      {(!methods?.bankAccounts?.length && !methods?.creditCards?.length) && (
        <p className="text-sm text-text-secondary py-4 text-center">No payment methods on file</p>
      )}

      {/* Add Payment Method Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <div className="flex items-center justify-center h-screen">
          <div className="bg-surface rounded-2xl p-6 max-w-[560px] w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg text-text-primary">Add Payment Method</h3>
              <button onClick={handleCloseModal} className="text-text-secondary hover:text-text-primary">
                <LuX size={18} />
              </button>
            </div>

            {/* Tab selector */}
            <div className="flex gap-0 mb-6 border-b border-divider">
              {showBank && (
                <button
                  onClick={() => { setActiveTab('bank'); setErrors({}) }}
                  className={`py-2 px-4 text-sm font-medium border-b-2 -mb-px ${
                    currentTab === 'bank' ? 'border-primary-main text-primary-main' : 'border-transparent text-text-secondary'
                  }`}
                >
                  Bank Account (ACH)
                </button>
              )}
              {showCard && (
                <button
                  onClick={() => { setActiveTab('card'); setErrors({}) }}
                  className={`py-2 px-4 text-sm font-medium border-b-2 -mb-px ${
                    currentTab === 'card' ? 'border-primary-main text-primary-main' : 'border-transparent text-text-secondary'
                  }`}
                >
                  Credit Card
                </button>
              )}
            </div>

            {/* Bank Account Form */}
            {currentTab === 'bank' && (
              <form onSubmit={handleSubmitBank} className="space-y-4">
                {renderInput('Bank Name', 'bankName', bankForm.bankName, updateBankField, 'text', 'e.g. Chase Bank')}
                {renderInput('Name on Account', 'nameOnAccount', bankForm.nameOnAccount, updateBankField, 'text', 'John Doe')}
                <div className="grid grid-cols-2 gap-4">
                  {renderInput('Routing Number', 'routingNumber', bankForm.routingNumber, updateBankField, 'text', '9 digits')}
                  {renderInput('Account Number', 'accountNumber', bankForm.accountNumber, updateBankField, 'text', 'Account number')}
                </div>
                <hr className="border-divider" />
                <p className="text-[13px] font-medium text-text-primary">Billing Address</p>
                {renderInput('Address', 'address', bankForm.address, updateBankField)}
                {renderInput('Address Line 2', 'address2', bankForm.address2, updateBankField, 'text', 'Apt, Suite (optional)')}
                <div className="grid grid-cols-3 gap-4">
                  {renderInput('City', 'city', bankForm.city, updateBankField)}
                  {renderInput('State', 'state', bankForm.state, updateBankField)}
                  {renderInput('Zip Code', 'zipcode', bankForm.zipcode, updateBankField)}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 bg-primary-main text-primary-contrast text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Adding...' : 'Add Bank Account'}
                </button>
              </form>
            )}

            {/* Credit Card Form */}
            {currentTab === 'card' && (
              <form onSubmit={handleSubmitCard} className="space-y-4">
                <div>
                  <label className="text-[13px] text-text-secondary block mb-1">Card Type</label>
                  <select
                    value={cardForm.provider}
                    onChange={(e) => updateCardField('provider', e.target.value)}
                    className="w-full border border-divider rounded-xl px-3 py-2 text-sm text-text-primary bg-paper focus:outline-none focus:border-primary-main"
                  >
                    <option value="Visa">Visa</option>
                    <option value="Mastercard">Mastercard</option>
                    <option value="American Express">American Express</option>
                    <option value="Discover">Discover</option>
                  </select>
                </div>
                {renderInput('Name on Card', 'nameOnCard', cardForm.nameOnCard, updateCardField, 'text', 'John Doe')}
                {renderInput('Card Number', 'cardNumber', cardForm.cardNumber, updateCardField, 'text', 'Card number')}
                <div className="grid grid-cols-2 gap-4">
                  {renderInput('CVV', 'cvv', cardForm.cvv, updateCardField, 'text', '3-4 digits')}
                  {renderInput('Expiry Date', 'expiryDate', cardForm.expiryDate, updateCardField, 'text', 'MM/YY')}
                </div>
                <hr className="border-divider" />
                <p className="text-[13px] font-medium text-text-primary">Billing Address</p>
                {renderInput('Address', 'address', cardForm.address, updateCardField)}
                {renderInput('Address Line 2', 'address2', cardForm.address2, updateCardField, 'text', 'Apt, Suite (optional)')}
                <div className="grid grid-cols-3 gap-4">
                  {renderInput('City', 'city', cardForm.city, updateCardField)}
                  {renderInput('State', 'state', cardForm.state, updateCardField)}
                  {renderInput('Zip Code', 'zipcode', cardForm.zipcode, updateCardField)}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 bg-primary-main text-primary-contrast text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Adding...' : 'Add Credit Card'}
                </button>
              </form>
            )}
          </div>
        </div>
      </Modal>
    </>
  )
})

export default PortalPaymentMethods
